import { useState } from 'react'
import { Badge, Button } from '../../shared/ui'
import { toast } from '../../lib/toast'
import {
  connectEmail,
  describeSource,
  disconnectEmail,
  PROVIDERS,
  syncEmailNow,
  type BusinessEmail,
  type BusinessSource,
  type EmailProvider,
} from '../../lib/sources'

/**
 * One business's email: every inbox it has connected, and adding more.
 *
 * A business connects as many inboxes as it sends from (Gmail / Google
 * Workspace and Zoho Mail, mixed). Each inbox can sync now, sign in again or
 * be disconnected on its own. "Add another inbox" runs the same sign-in for
 * the next address. Members who are not owner/admin see the status only.
 */

const PROVIDER_ORDER: EmailProvider[] = ['gmail', 'zoho']

function canManage(g: { role: BusinessSource['role'] }): boolean {
  return g.role === 'owner' || g.role === 'admin'
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const h = Math.round(mins / 60)
  if (h < 24) return `${h} h ago`
  return `${Math.round(h / 24)} d ago`
}

function statusBadge(s: BusinessSource) {
  switch (s.status) {
    case 'connected':
      return <Badge tone="ok">Connected</Badge>
    case 'syncing':
      return <Badge tone="warn">Syncing</Badge>
    case 'error':
      return <Badge tone="err">Sync problem</Badge>
    case 'needs_reconnect':
      return <Badge tone="err">Sign in again</Badge>
    default:
      return <Badge tone="muted">Not connected</Badge>
  }
}

interface ProviderPickerProps {
  workspaceId: string
  exclude?: EmailProvider | null
  busy: boolean
  onStart: (p: EmailProvider) => void
}

/** The two provider buttons, each with a one-line hint. */
export function ProviderPicker({ exclude, busy, onStart }: ProviderPickerProps) {
  return (
    <ul className="src-providers" aria-label="Email providers">
      {PROVIDER_ORDER.filter((p) => p !== exclude).map((p) => (
        <li key={p} className="src-provider">
          <div className="src-provider-text">
            <div className="src-provider-name">{PROVIDERS[p].label}</div>
            <div className="src-provider-hint">{PROVIDERS[p].hint}</div>
          </div>
          <Button
            onClick={() => onStart(p)}
            disabled={busy}
            aria-label={`Connect ${PROVIDERS[p].label}`}
          >
            Connect
          </Button>
        </li>
      ))}
    </ul>
  )
}

interface CardProps {
  business: BusinessEmail
  onChanged: () => void
  /** Hide the business name (used inside onboarding, where it is the heading). */
  compact?: boolean
}

async function runSafely(fn: () => Promise<void>) {
  try {
    await fn()
  } catch (e) {
    toast(e instanceof Error ? e.message : 'Something went wrong.', 'error')
  }
}

function MailboxRow({
  box,
  manage,
  onChanged,
}: {
  box: BusinessSource
  manage: boolean
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const label = box.email ?? PROVIDERS[box.provider!].label

  async function run(fn: () => Promise<void>) {
    if (busy) return
    setBusy(true)
    await runSafely(fn)
    setBusy(false)
  }

  const reconnect = () => run(() => connectEmail(box.provider!, box.workspace_id))
  const sync = () =>
    run(async () => {
      const n = await syncEmailNow(box)
      toast(`Synced ${label}, ${n} new message${n === 1 ? '' : 's'}`, 'success')
      onChanged()
    })
  const disconnect = () =>
    run(async () => {
      const ok = window.confirm(
        `Disconnect ${label} from ${box.workspace_name}? ` +
          'Emails synced from this inbox are removed from Hunts. The mailbox itself is untouched.',
      )
      if (!ok) return
      await disconnectEmail(box)
      toast(`${label} disconnected`, 'success')
      onChanged()
    })

  return (
    <li className="src-box" aria-label={label}>
      <div className="src-line">
        <span className="src-value">
          {describeSource(box)}
          <span className="src-dim"> · {PROVIDERS[box.provider!].label}</span>
        </span>
        {statusBadge(box)}
      </div>
      <div className="src-meta">Last sync {timeAgo(box.last_sync_at)}</div>
      {box.last_sync_error && box.status === 'error' && (
        <div className="src-error" role="alert">
          {box.last_sync_error}
        </div>
      )}
      {manage && (
        <div className="src-actions">
          {box.needs_reconnect ? (
            <Button
              onClick={() => void reconnect()}
              disabled={busy}
              aria-label={`Sign in again to ${label}`}
            >
              Sign in again
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => void sync()}
              disabled={busy}
              aria-label={`Sync ${label} now`}
            >
              Sync now
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => void disconnect()}
            disabled={busy}
            aria-label={`Disconnect ${label}`}
          >
            Disconnect
          </Button>
        </div>
      )}
    </li>
  )
}

export function EmailSourceCard({ business: g, onChanged, compact }: CardProps) {
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  const manage = canManage(g)
  const boxes = g.mailboxes

  const start = async (p: EmailProvider) => {
    if (busy) return
    setBusy(true)
    await runSafely(async () => {
      await connectEmail(p, g.workspace_id)
      setAdding(false)
    })
    setBusy(false)
  }

  const overall: BusinessSource | null =
    boxes.find((b) => b.status === 'needs_reconnect') ??
    boxes.find((b) => b.status === 'error') ??
    boxes.find((b) => b.status === 'syncing') ??
    boxes[0] ??
    null

  return (
    <li className={`src-card${g.is_current ? ' is-current' : ''}`}>
      <div className="src-card-head">
        {!compact && <h3 className="src-card-name">{g.workspace_name}</h3>}
        <div className="src-badges">
          {!compact && g.workspace_kind === 'personal' && <Badge>Personal</Badge>}
          {!compact && g.is_current && <Badge tone="ok">Current</Badge>}
          {boxes.length > 1 ? (
            <Badge>{boxes.length} inboxes</Badge>
          ) : (
            statusBadge(overall ?? ({ status: 'none' } as BusinessSource))
          )}
        </div>
      </div>

      {boxes.length === 0 ? (
        <div className="src-line">
          <span className="src-kind">Email</span>
          <span className="src-value">Not connected yet</span>
        </div>
      ) : (
        <ul className="src-boxes" aria-label={`Inboxes for ${g.workspace_name}`}>
          {boxes.map((b) => (
            <MailboxRow key={b.source_id} box={b} manage={manage} onChanged={onChanged} />
          ))}
        </ul>
      )}

      {!manage && (
        <p className="src-meta">Only an owner or admin of this business can change its email.</p>
      )}

      {manage && boxes.length === 0 && (
        <ProviderPicker workspaceId={g.workspace_id} busy={busy} onStart={(p) => void start(p)} />
      )}

      {manage && boxes.length > 0 && (
        <div className="src-actions">
          <Button
            variant="secondary"
            onClick={() => setAdding((v) => !v)}
            disabled={busy}
            aria-expanded={adding}
          >
            Add another inbox
          </Button>
        </div>
      )}
      {manage && boxes.length > 0 && adding && (
        <ProviderPicker workspaceId={g.workspace_id} busy={busy} onStart={(p) => void start(p)} />
      )}
    </li>
  )
}
